"use client"
import React, { useState } from "react";
import { apiFetch } from "@/utils/api";
import Link from "next/link";
import { LoaderIcon } from "@/utils/icons";
import { useRouter } from "next/navigation";
import { EyeIcon, EyeOffIcon } from "@/utils/icons";
import { useAuth } from "@/hooks/useAuth";

export default function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const { login: setAuthUser } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (!identifier.trim()) {
      setError("Please enter your username or email.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({ identifier, password }),
      });
      setSuccess(true);
      setError("");
      let user: { role?: string, token?: string } | undefined = undefined;
      let token: string | undefined = undefined;
      const resUnknown: unknown = res;
      if (res && typeof res === 'object') {
        if ('user' in res && typeof (resUnknown as { user: unknown }).user === 'object') {
          user = (resUnknown as { user: { role?: string, token?: string } }).user;
          token = (resUnknown as { token?: string }).token;
        } else if ('data' in res && (resUnknown as { data: unknown }).data && typeof (resUnknown as { data: { user: unknown } }).data === 'object' && 'user' in (resUnknown as { data: { user: unknown } }).data) {
          user = (resUnknown as { data: { user: { role?: string, token?: string }, token?: string } }).data.user;
          token = (resUnknown as { data: { token?: string } }).data.token;
        }
      }
      // Store session in sessionStorage as well as localStorage, always including the token
      if (user && token) {
        const maybeUser = user as Partial<import("@/hooks/useAuth").AuthUser>;
        if (maybeUser.id && maybeUser.username && maybeUser.email && maybeUser.role) {
          const userWithToken: import("@/hooks/useAuth").AuthUser = {
            id: maybeUser.id,
            username: maybeUser.username,
            email: maybeUser.email,
            role: maybeUser.role as import("@/hooks/useAuth").UserRole,
            token: token,
          };
          localStorage.setItem('authUser', JSON.stringify(userWithToken));
          sessionStorage.setItem('authUser', JSON.stringify(userWithToken));
          setAuthUser(userWithToken);
        }
      }
      if (user && ["admin", "manager", "staff"].includes(user.role || "")) {
        router.replace("/admin");
      } else {
        router.replace("/");
      }
    } catch (err: unknown) {
      const msg = (err as Error).message || "Login failed";
      if (msg.toLowerCase().includes("not a user")) {
        setError("seems like not a user, register first");
      } else if (msg.toLowerCase().includes("invalid credentials") || msg.toLowerCase().includes("password incorrect")) {
        setError("Invalid credentials");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-16 px-4">
      <h1 className="text-2xl font-bold mb-6">Login</h1>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 bg-white rounded-lg shadow p-6" aria-label="Login form">
        <input
          type="text"
          placeholder="Username or Email"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-base bg-white transition"
          required
          aria-label="Username or Email"
        />
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-base bg-white transition w-full pr-10"
            required
            aria-label="Password"
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            tabIndex={-1}
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
          </button>
        </div>
        <button
          type="submit"
          className="bg-primary-500 text-white px-6 py-2 rounded font-semibold hover:bg-primary-600 transition flex items-center justify-center gap-2 disabled:opacity-60"
          disabled={loading}
        >
          {loading && <LoaderIcon className="animate-spin h-5 w-5" />} LOGIN
        </button>
        {error && <div className="text-red-500 text-sm mt-2" role="alert">{error}</div>}
        {success && <div className="text-green-600 text-sm mt-2" role="status">Login successful!</div>}
        <div className="text-sm text-gray-500 mt-2">
          <Link href="/forgot-password" className="text-primary-600 hover:underline">Forgot password?</Link>
        </div>
        <div className="text-sm text-gray-500 mt-2">
          Don&apos;t have an account? <Link href="/register" className="text-primary-600 hover:underline">Register</Link>
        </div>
      </form>
    </div>
  );
} 